import { useState, useEffect, useCallback } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { Toast } from '@/components/ui/Toast/Toast';
import type { Organization } from '@/types/events';
import { logger } from '@/utils/logger';
import { insertNotification } from '@/utils/notificationInsert';
import { fetchProfileDisplayName } from '@/utils/notificationHelpers';
import { ShiftList } from '@/components/common/ShiftList';
import { useEventShifts } from '@/hooks/useEventShifts';

interface VolunteerJoinEventModalProps {
  isOpen: boolean;
  event: Organization | null;
  onClose: () => void;
  onSuccess: () => void;
}

function toggleInList(list: string[], item: string): string[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

type CapacityState = 'unchecked' | 'loading' | 'open' | 'full';

export function VolunteerJoinEventModal({ isOpen, event, onClose, onSuccess }: VolunteerJoinEventModalProps) {
  const { user } = useAuth();
  const { showToast, hideToast, toast } = useToast();
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedShiftIds, setSelectedShiftIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [capacityState, setCapacityState] = useState<CapacityState>('unchecked');

  useEffect(() => {
    if (isOpen && event?.id) {
      setSelectedDepartments([]);
      setSelectedShiftIds([]);
    }
  }, [isOpen, event?.id]);

  const departments = event?.departments ?? [];
  const { shifts, loading: shiftsLoading } = useEventShifts(event?.id, isOpen);
  const hasDepartments = departments.length > 0;

  useEffect(() => {
    if (!isOpen || !event?.id || !hasDepartments) {
      setCapacityState('unchecked');
      return;
    }

    let cancelled = false;
    setCapacityState('loading');

    const run = async () => {
      try {
        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select('slots')
          .eq('id', event.id)
          .single();
        const { data: volunteersData, error: volunteersError } = await supabase
          .from('volunteers')
          .select('id')
          .eq('event_id', event.id)
          .eq('is_active', true);
        if (cancelled) return;
        if (eventError || volunteersError) {
          setCapacityState('open');
          return;
        }
        const slots = eventData?.slots;
        const count = volunteersData?.length ?? 0;
        if (slots != null && count >= Number(slots)) setCapacityState('full');
        else setCapacityState('open');
      } catch {
        if (!cancelled) setCapacityState('open');
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, event?.id, hasDepartments]);

  const canShowJoinForm = hasDepartments && capacityState === 'open';
  const canSubmit =
    canShowJoinForm && selectedDepartments.length > 0 && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!user?.id || !event?.id || !canSubmit) return;
    setSubmitting(true);
    try {
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('slots')
        .eq('id', event.id)
        .single();
      if (eventError) {
        showToast('Unable to verify event availability. Please try again.', 'error');
        return;
      }
      const { data: volunteersData, error: volunteersError } = await supabase
        .from('volunteers')
        .select('id')
        .eq('event_id', event.id)
        .eq('is_active', true);
      if (volunteersError) {
        showToast('Unable to verify event availability. Please try again.', 'error');
        return;
      }
      const currentVolunteerCount = volunteersData?.length ?? 0;
      if (eventData?.slots != null && currentVolunteerCount >= Number(eventData.slots)) {
        showToast('This event has just reached its maximum capacity. Please try another event.', 'error');
        return;
      }

      const { data: existingApplication, error: checkError } = await supabase
        .from('volunteer_applications')
        .select('id, status')
        .eq('volunteer_id', user.id)
        .eq('event_id', event.id)
        .maybeSingle();

      if (checkError) {
        showToast('Failed to check existing application. Please try again.', 'error');
        return;
      }
      if (existingApplication) {
        if (existingApplication.status === 'pending') {
          showToast('You already have a pending application for this event.', 'info');
        } else if (existingApplication.status === 'accepted') {
          showToast('You are already accepted for this event.', 'info');
        } else {
          showToast('Your previous application for this event was rejected.', 'error');
        }
        return;
      }

      const { data: application, error } = await supabase
        .from('volunteer_applications')
        .insert({
          volunteer_id: user.id,
          event_id: event.id,
          selected_departments: selectedDepartments,
          // Only sent when shifts were picked, which needs get_event_shifts to exist. Applying
          // keeps working if this build ships before 20260917180000_event_shifts.sql is applied.
          ...(selectedShiftIds.length > 0 ? { selected_shift_ids: selectedShiftIds } : {}),
          status: 'pending',
          created_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error) {
        showToast(error.message || 'Failed to submit application. Please try again.', 'error');
        return;
      }

      const { data: eventRow } = await supabase
        .from('events')
        .select('id, name, created_by')
        .eq('id', event.id)
        .maybeSingle();

      const orgId = eventRow?.created_by ?? event.created_by;
      if (orgId && application?.id) {
        const volunteerName = await fetchProfileDisplayName(user.id);
        const eventLabel = eventRow?.name?.trim() || event.name?.trim() || 'your event';
        insertNotification({
          user_id: orgId,
          title: 'New event application',
          message: `${volunteerName} applied to join ${eventLabel}.`,
          type: 'event_application_pending',
          data: {
            application_id: application.id,
            event_id: event.id,
            volunteer_id: user.id,
            event_name: eventLabel,
            volunteer_name: volunteerName,
          },
          read: false,
        });
      }

      showToast('Application submitted!', 'success');
      onSuccess();
      onClose();
    } catch (e) {
      logger.error('VolunteerJoinEventModal submit', e);
      showToast('Failed to submit application. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [
    user?.id,
    event,
    canSubmit,
    selectedDepartments,
    selectedShiftIds,
    showToast,
    onSuccess,
    onClose,
  ]);

  if (!isOpen || !event) return null;

  return (
    <>
      <Flex
        position="fixed"
        inset="0"
        bg="rgba(0,0,0,0.45)"
        align="center"
        justify="center"
        zIndex={10000}
        px="4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <Box
          bg="white"
          borderRadius="24px"
          w="full"
          maxW="440px"
          maxH="90vh"
          overflow="hidden"
          display="flex"
          flexDirection="column"
          boxShadow="0 20px 40px rgba(0,0,0,0.15)"
          onClick={(e) => e.stopPropagation()}
        >
          <Flex align="center" justify="space-between" px="5" py="4" borderBottom="1px solid" borderColor="volyx.mediumGray">
            <Text fontSize="lg" fontWeight="bold" color="volyx.text" lineClamp={1} pr="2">
              Join {event.name}
            </Text>
            <chakra.button
              type="button"
              aria-label="Close"
              onClick={onClose}
              borderRadius="md"
              p="1"
              _hover={{ bg: 'volyx.lightGray' }}
            >
              <X size={22} color="#1F2932" />
            </chakra.button>
          </Flex>

          <Box px="5" py="4" overflowY="auto" flex="1">
            {!hasDepartments && (
              <Text fontSize="sm" color="volyx.textSecondary" mb="4">
                This event does not have departments set up yet. You cannot apply until the organizer adds departments.
              </Text>
            )}

            {hasDepartments && capacityState === 'loading' && (
              <Text fontSize="sm" color="volyx.textSecondary" mb="4">
                Checking availability…
              </Text>
            )}

            {hasDepartments && capacityState === 'full' && (
              <Text fontSize="sm" color="volyx.textSecondary" mb="4">
                This event has reached its volunteer capacity. You cannot apply right now.
              </Text>
            )}

            {canShowJoinForm && (
              <>
                <Text fontSize="sm" fontWeight="semibold" color="volyx.text" mb="2">
                  Departments
                </Text>
                <Text fontSize="xs" color="volyx.textSecondary" mb="2" m="0">
                  Choose at least one.
                </Text>
                <Flex flexWrap="wrap" gap="2" mb="6">
                  {departments.map((d) => {
                    const selected = selectedDepartments.includes(d);
                    return (
                      <chakra.button
                        key={d}
                        type="button"
                        px="3"
                        py="2"
                        borderRadius="xl"
                        fontSize="sm"
                        fontWeight="medium"
                        border="2px solid"
                        borderColor={selected ? 'volyx.primary' : 'volyx.mediumGray'}
                        bg={selected ? 'volyx.primaryLight' : 'white'}
                        color="volyx.text"
                        onClick={() => setSelectedDepartments((prev) => toggleInList(prev, d))}
                      >
                        {d}
                      </chakra.button>
                    );
                  })}
                </Flex>

                {shiftsLoading && shifts.length === 0 && (
                  <Text fontSize="sm" color="volyx.textSecondary">
                    Loading shifts…
                  </Text>
                )}

                {shifts.length > 0 && (
                  <>
                    <Text fontSize="sm" fontWeight="semibold" color="volyx.text" mb="2">
                      Shifts{' '}
                      <Text as="span" fontWeight="normal" color="volyx.textSecondary">
                        (optional)
                      </Text>
                    </Text>
                    <Text fontSize="xs" color="volyx.textSecondary" mb="3">
                      Pick the shifts you can work. The organizer confirms them when they accept you.
                    </Text>
                    <ShiftList
                      shifts={shifts}
                      selectedIds={selectedShiftIds}
                      onToggle={(id) => setSelectedShiftIds((prev) => toggleInList(prev, id))}
                      blockFull
                    />
                  </>
                )}
              </>
            )}
          </Box>

          <Flex gap="3" px="5" py="4" borderTop="1px solid" borderColor="volyx.mediumGray">
            <chakra.button
              type="button"
              flex="1"
              py="3"
              borderRadius="xl"
              fontWeight="semibold"
              border="2px solid"
              borderColor="volyx.mediumGray"
              bg="white"
              color="volyx.text"
              onClick={onClose}
            >
              Cancel
            </chakra.button>
            <chakra.button
              type="button"
              flex="1"
              py="3"
              borderRadius="xl"
              fontWeight="semibold"
              bg={canSubmit ? 'volyx.primary' : 'volyx.textSecondary'}
              color="white"
              cursor={canSubmit ? 'pointer' : 'not-allowed'}
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </chakra.button>
          </Flex>
        </Box>
      </Flex>
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
    </>
  );
}
